import {
  deleteCartProduct,
  getCartId,
  getProductDetail,
  updateCartProductQty,
  useCartPoints,
  useCartProducts,
} from "@api/customer/cart";
import { useRelatedProducts } from "@api/products/products";
import Loader from "@components/atoms/Loader";
import { CART_CHECKOUT_ROUTE_PATH } from "@const/route-paths.const";
import { ProductDetailShort } from "@interfaces/dto/product-detail.dto";
import { numberToMoneyString } from "@utils/numbers.util";
import { commonToast, errorToast } from "@utils/toast";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { RECOMMEND_CATEGORY_ID as ROOT_CATEGORY_ID } from "@const/recommend-category.const";
import RelatedProducts from "@components/features/product-detail/related-product/RelatedProducts";
import TagManager, { DataLayerArgs } from "react-gtm-module"; // DataLayer GTM
import ActionNavigate from "../navigation/ActionNavigate";
import {
  isWebview,
  clickHomeButtonNative,
  updateCartCountNative,
} from "@utils/webview.util";
import SkeletonProductCart from "@components/atoms/skeleton/ui-product-cart";
import SkeletonProductItem from "@components/atoms/skeleton/ui-product-item";

const CartProducts = () => {
  const router = useRouter();
  const [isFirstLoaded, setIsFirstLoaded] = useState<boolean>(false);
  const [isLoadDetail, setIsLoadDetail] = useState<boolean>(false);
  const [cartId, setCartId] = useState<number>();
  const [updatingCart, setUpdatingCart] = useState<boolean>(false);
  const [productsMapping, setProductsMapping] = useState<
    (ProductDetailShort | undefined)[]
  >([]);
  const { data, isLoading, error, mutate } = useCartProducts();

  // Relate product
  const UPSELL_AND_RECOMMEND_PRODUCT_PAGE_SIZE = 5;
  const [randomPage, setRandomPage] = useState(1);

  function randomPageInRange(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  useEffect(() => {
    setRandomPage(randomPageInRange(1, 50));
    return () => {};
  }, []);

  const { data: relatedProductsData, isLoading: relatedProductsIsLoading } =
    useRelatedProducts({
      categoryId: ROOT_CATEGORY_ID,
      productDisplayConfig: {
        currentPage: randomPage > 0 ? randomPage : 1,
        pageSize: UPSELL_AND_RECOMMEND_PRODUCT_PAGE_SIZE,
        sortBy: "position",
      },
    });
  const { data: cartPointsData, mutate: cartPointsMutate } = useCartPoints();

  useEffect(() => {
    const fetchCartId = async () => await getCartId();

    fetchCartId()
      .then((response) => setCartId(Number(response.data)))
      .catch(() => router.push("/404"));
  }, [router]);

  useEffect(() => {
    if (!isFirstLoaded && data && data.items && data.items.length > 0) {
      setProductsMapping([]);
      const fetchProducts = async () => {
        const products = await Promise.all(
          data.items.map(
            async (item) => await getProductDetail(item.item_id.toString())
          )
        );
        setProductsMapping(products);
        setIsFirstLoaded(true);
        setIsLoadDetail(true);
      };
      fetchProducts();
    }
  }, [data, isFirstLoaded]);

  const handleUpdateQty = async (
    productId: number,
    sku: string,
    cartId: number,
    qty: number
  ) => {
    setUpdatingCart(true);
    const { isOk, message } = await updateCartProductQty(productId, {
      cartItem: {
        sku: sku,
        qty: qty,
        quote_id: cartId,
      },
    });

    if (isOk) {
      mutate();
      cartPointsMutate();
      if (isWebview()) {
        updateCartCountNative();
      }
    } else {
      errorToast(message);
    }

    setUpdatingCart(false);
  };

  const handleDeleteCartProduct = async (productId: number) => {
    const { isOk, message } = await deleteCartProduct(productId);
    if (isOk) {
      mutate();
      cartPointsMutate();
      commonToast({ isOk, message });
      if (isWebview()) {
        updateCartCountNative();
      }
    } else {
      errorToast(message);
    }
  };

  if (error) {
    router.push("/404");
  }

  // Datalayer GTM
  useEffect(() => {
    const skuMapping = productsMapping.map((value) => {
      return value?.sku;
    });

    const itemsInCart = productsMapping.map((value) => {
      let findItem = data.items?.find(
        ({ item_id }) => item_id === value?.item_id
      );
      setIsLoadDetail(true);

      return {
        item_id: value?.sku,
        item_name: findItem?.name,
        quantity: findItem?.qty,
        price: value?.is_free ? 0 : findItem?.row_total,
      };
    });

    if (
      data?.total_segments.find(({ code }) => code === "subtotal")?.value &&
      skuMapping.length > 0
    ) {
      const variables: Record<string, any> = {
        event: "topvalue.next",
        PageType: "Cartpage",
        ProductIDList: skuMapping,
        TotalPrice: data?.total_segments.find(
          ({ code }) => code === "grand_total"
        )?.value,
        items: itemsInCart,
      };
      const dataLayerArgs: DataLayerArgs = {
        dataLayer: { variables },
      };
      TagManager.dataLayer(dataLayerArgs);
    }
  }, [data?.total_segments, productsMapping, data?.items]);

  let discount = data?.total_segments.find(({ code }) => code === "discount");
  let shippingEstimate = data?.total_segments.find(
    ({ code }) => code === "shipping"
  );

  return (
    <>
      <section className="mainContent">
        <div className="container-fluid">
          <div className="row">
            <div className="col">
              <h1>
                รถเข็น{" "}
                {data && data.items_qty > 0 ? <>({data.items_qty})</> : ""}
              </h1>
              <div className="d-none d-lg-block">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link href="/">
                        <a>หน้าแรก</a>
                      </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      รถเข็นสินค้า
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      {cartId && (
        <>
          {data && data?.items === null ? (
            <section className="product-section">
              <div className="container-fluid">
                <div className="product-empty-cart rounded-2">
                  <div className="row">
                    <div className="col-12 text-center">
                      <img
                        src="/assets/images/icon-empty-cart.png"
                        className="icon-empty-cart mb-4"
                        alt=""
                      />
                      <p className="mb-5">คุณยังไม่มีสินค้าในตะกร้า</p>
                      <Link href="/">
                        <a
                          className="btn btn-block btn-red2"
                          onClick={(e) => {
                            if (isWebview()) {
                              e.preventDefault();
                              clickHomeButtonNative();
                            }
                          }}
                        >
                          เลือกซื้อสินค้า
                        </a>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <>
              {isFirstLoaded && isLoading ? (
                <Loader />
              ) : (
                <section className="product-section">
                  <div className="container-fluid">
                    <div className="product-cart">
                      <div className="d-none d-lg-flex bg-white mb-3 fs-6 rounded-2">
                        <div className="container">
                          <div className="row text-end py-3">
                            <div className="col col-lg-6 col-sm-5 text-start">
                              สินค้า
                            </div>
                            <div className="col col-sm-2">ราคาต่อชิ้น</div>
                            <div className="col col-sm-2 text-center">
                              จำนวน
                            </div>
                            <div className="col col-lg-1 col-sm-2">ยอดรวม</div>
                            <div className="col col-sm-1"></div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white mb-3 rounded-2">
                        {isLoadDetail ? (
                          <div className="table-cart fs-6 container py-3">
                            {data?.items.map((item, idx) => {
                              const mapping = productsMapping.find(
                                (mapping) =>
                                  mapping && mapping.item_id === item.item_id
                              );
                              const options:
                                | { value: string; label: string }[]
                                | undefined = JSON.parse(item.options);

                              return (
                                <div
                                  className={`row ${
                                    idx > 0 && "pt-3"
                                  } pb-3 border-bottom`}
                                  key={idx}
                                >
                                  <div className="col-12 col-lg-6">
                                    <div className="row">
                                      <div className="col-3 col-lg-3 col-sm-2">
                                        <Link
                                          href={`/products/${mapping?.url_key}`}
                                        >
                                          <img
                                            src={mapping?.image}
                                            alt={item.name}
                                            className="img-fluid border"
                                          />
                                        </Link>
                                      </div>
                                      <div className="col-9 col-lg-9 col-sm-10 pt-lg-4">
                                        <Link
                                          href={`/products/${mapping?.url_key}`}
                                        >
                                          <a>{item.name}</a>
                                        </Link>
                                        <small className="d-block pt-2">
                                          SKU : {mapping?.sku}
                                        </small>
                                        {options && options.length > 0 && (
                                          <small className="d-block pb-2">
                                            {options[0].label} :{" "}
                                            {options[0].value}
                                          </small>
                                        )}
                                        <b className="d-inline-block d-lg-none pt-3 mb-1">
                                          {numberToMoneyString(item.price, 0)}.-
                                        </b>
                                        {/* {item.extension_attributes
                                          .regular_price !== item.price && (
                                          <small className="text-red d-flex d-lg-none gap-1">
                                            <del>
                                              {
                                                item.extension_attributes
                                                  .regular_price
                                              }
                                              .-
                                            </del>
                                            <small>
                                              ประหยัด
                                              {item.extension_attributes
                                                .regular_price - item.price}
                                              .-
                                            </small>
                                          </small>
                                        )} */}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-9 col-md-2 d-none d-lg-block text-md-end pt-lg-4">
                                    <b className="d-none d-md-block">
                                      {numberToMoneyString(item.price, 0)}.-
                                    </b>
                                    {/* {item.extension_attributes.regular_price !==
                                      item.price && (
                                      <>
                                        <del>
                                          {
                                            item.extension_attributes
                                              .regular_price
                                          }
                                          .-
                                        </del>
                                        <small className="text-red">
                                          {" "}
                                          ประหยัด{" "}
                                          {item.extension_attributes
                                            .regular_price - item.price}
                                          .-
                                        </small>
                                      </>
                                    )} */}
                                  </div>
                                  <div className="col-4 col-md-2 offset-3 offset-lg-0 offset-sm-2 pt-sm-3">
                                    <div className="input-group group-quantity m-0 mt-3 m-md-auto mt-md-0 rounded-3">
                                      <input
                                        type="button"
                                        defaultValue="-"
                                        className="button-minus btn-white fs-3"
                                        data-field="quantity"
                                        disabled={
                                          updatingCart || item.qty === 1
                                        }
                                        onClick={() =>
                                          handleUpdateQty(
                                            item.item_id,
                                            mapping!.sku,
                                            cartId,
                                            item.qty - 1
                                          )
                                        }
                                      />
                                      <input
                                        type="number"
                                        step={1}
                                        max={100}
                                        value={item.qty}
                                        name="quantity"
                                        className="quantity-field text-center"
                                        onChange={() => undefined}
                                        readOnly
                                      />
                                      <input
                                        type="button"
                                        defaultValue="+"
                                        className="button-plus btn-white fs-4"
                                        data-field="quantity"
                                        disabled={updatingCart}
                                        onClick={() =>
                                          handleUpdateQty(
                                            item.item_id,
                                            mapping!.sku,
                                            cartId,
                                            item.qty + 1
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div className="col-6 col-lg-1 col-md-2 text-end d-none d-lg-block pt-lg-4 pt-sm-2">
                                    <b className="text-red">
                                      {item.discount_amount === item.row_total
                                        ? "0.-"
                                        : `${numberToMoneyString(
                                            item.row_total,
                                            0
                                          )}.-`}
                                    </b>
                                  </div>
                                  <div className="col-2 col-sm-1 text-center pt-1 pt-sm-3 pt-lg-4 mt-3 mt-sm-3 mt-md-0 ms-auto">
                                    <a
                                      onClick={() =>
                                        handleDeleteCartProduct(item.item_id)
                                      }
                                    >
                                      <img
                                        src="/assets/images/icon-delete.svg"
                                        alt=""
                                      />
                                    </a>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="bg-white mb-3 rounded-2">
                            <div className="table-cart container">
                              <SkeletonProductCart count={data?.items.length} />
                            </div>
                          </div>
                        )}

                        {/* Summary */}
                        {cartPointsData && (
                          <div className="d-lg-none px-3 pb-3">
                            <p className="m-0">
                              <i className="icon icon-coin" /> Topvalue Coins
                              ที่จะได้รับ{" "}
                              <span className="text-red">
                                {cartPointsData.caption_text}
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="bg-white rounded-2">
                        <div className="p-3">
                          <div className="row">
                            <div className="col-12 col-md-5 col-lg-4">
                              <h4 className="m-0">สรุปรายการสั่งซื้อ</h4>
                            </div>
                          </div>
                        </div>
                        <hr className="m-0" />
                        <div className="bg-white p-30 container-fluid rounded-2">
                          <div className="row justify-content-md-end">
                            {cartPointsData && (
                              <div className="d-none d-lg-block col-12 col-lg-8 mb-3 pt-2">
                                <p>
                                  <i className="icon icon-coin" /> Topvalue
                                  Coins ที่จะได้รับ{" "}
                                  <span className="text-red">
                                    {cartPointsData.caption_text}
                                  </span>
                                </p>
                              </div>
                            )}
                            <div className="col-12 col-lg-4 mb-3">
                              <table className="table-total">
                                <tbody>
                                  <tr>
                                    <td>ยอดรวม</td>
                                    <td className="text-end">
                                      {numberToMoneyString(
                                        data?.total_segments.find(
                                          ({ code }) => code === "subtotal"
                                        )!.value,
                                        0
                                      )}
                                      .-
                                    </td>
                                  </tr>
                                  {/* {shippingEstimate &&
                                  shippingEstimate?.value > 0 && (
                                    <tr>
                                      <td>ค่าจัดส่ง</td>
                                      <td className="text-end">
                                        {shippingEstimate?.value.toLocaleString()}
                                        .-
                                      </td>
                                    </tr>
                                  )} */}
                                  {discount && discount?.value < 0 && (
                                    <tr>
                                      <td>ส่วนลด</td>
                                      <td className="text-end text-red">
                                        {discount?.value.toLocaleString()}.-
                                      </td>
                                    </tr>
                                  )}

                                  <tr>
                                    <td>ยอดรวมสุทธิ</td>
                                    <td className="text-end">
                                      <big>
                                        {numberToMoneyString(
                                          data?.total_segments.find(
                                            ({ code }) => code === "grand_total"
                                          )!.value,
                                          0
                                        )}
                                        .-
                                      </big>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        <hr className="m-0 d-none d-lg-block" />
                        <div className="p-3 text-end d-none d-lg-block">
                          <div className="row">
                            <div className="col-12 my-2">
                              <Link href={CART_CHECKOUT_ROUTE_PATH}>
                                <a className="btn btn-red btn-confirm me-sm-2">
                                  ดำเนินการสั่งซื้อ
                                </a>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Action Navigate */}
                      {/* <ActionNavigate
                        actTotal={
                          data?.total_segments.find(
                            ({ code }) => code === "grand_total"
                          )!.value
                        }
                        actLink={CART_CHECKOUT_ROUTE_PATH}
                        actTitle="ยืนยันรายการสั่งซื้อ"
                      /> */}
                    </div>
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}

      {/* Related Product */}
      <div className="container-fluid d-none d-lg-block">
        <section className="product-section">
          <div className="row">
            {relatedProductsIsLoading ? (
              <SkeletonProductItem />
            ) : (
              relatedProductsData?.items && (
                <RelatedProducts
                  dataRelatedProduct={relatedProductsData?.items}
                />
              )
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default CartProducts;
